import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CropMarks, RegistrationDot, MicroLabel } from "@/components/decor/CropMarks";
import { useIsMobile, useReducedMotion, fadeUp, maskReveal } from "@/lib/motion";

const HeroCanvas = lazy(() => import("@/components/three/HeroCanvas"));

// Static SVG composition shown on mobile / reduced motion / R3F fallback
const StaticPuzzleScene = () => (
  <svg
    viewBox="0 0 600 600"
    className="w-full h-full"
    aria-hidden
    style={{ display: "block" }}
  >
    {/* faint grid */}
    <defs>
      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path
          d="M 40 0 L 0 0 0 40"
          fill="none"
          stroke="rgba(192,192,192,0.06)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="600" height="600" fill="url(#grid)" />

    {/* print sheet */}
    <g transform="translate(58 70) rotate(-7)">
      <rect width="220" height="290" fill="#F5F1E8" opacity="0.06" />
      <rect
        x="14"
        y="14"
        width="192"
        height="262"
        fill="none"
        stroke="rgba(245,241,232,0.45)"
      />
      <rect x="32" y="36" width="96" height="6" fill="#0072CE" />
      <rect x="32" y="50" width="156" height="3" fill="rgba(245,241,232,0.6)" />
      <rect x="32" y="60" width="120" height="3" fill="rgba(245,241,232,0.5)" />
      <rect x="32" y="200" width="50" height="50" fill="#D6007F" opacity="0.85" />
      <rect x="92" y="200" width="50" height="50" fill="#FFD200" opacity="0.85" />
    </g>

    {/* packaging box */}
    <g transform="translate(360 80)">
      <polygon
        points="0,40 110,0 220,40 220,180 110,220 0,180"
        fill="#141414"
        stroke="rgba(245,241,232,0.5)"
      />
      <polygon
        points="0,40 110,80 220,40"
        fill="rgba(245,241,232,0.04)"
        stroke="rgba(245,241,232,0.3)"
      />
      <line
        x1="110"
        y1="80"
        x2="110"
        y2="220"
        stroke="rgba(245,241,232,0.3)"
      />
      <rect x="50" y="120" width="40" height="6" fill="#B89B5E" />
    </g>

    {/* signage panel */}
    <g transform="translate(320 320)">
      <rect width="240" height="160" fill="#0A0A0A" stroke="rgba(245,241,232,0.45)" />
      <rect x="18" y="22" width="120" height="14" fill="#F5F1E8" />
      <rect x="18" y="48" width="180" height="6" fill="rgba(245,241,232,0.5)" />
      <rect x="18" y="60" width="140" height="6" fill="rgba(245,241,232,0.3)" />
      <circle cx="208" cy="132" r="14" stroke="#FFD200" fill="none" />
      <line x1="194" y1="132" x2="222" y2="132" stroke="#FFD200" />
      <line x1="208" y1="118" x2="208" y2="146" stroke="#FFD200" />
    </g>

    {/* gift silhouette */}
    <g transform="translate(70 380)">
      <rect width="180" height="140" fill="#141414" stroke="rgba(245,241,232,0.4)" />
      <line x1="90" y1="0" x2="90" y2="140" stroke="#B89B5E" />
      <line x1="0" y1="58" x2="180" y2="58" stroke="#B89B5E" />
      <path
        d="M68 0 Q90 -22 112 0"
        fill="none"
        stroke="#B89B5E"
      />
    </g>

    {/* CMYK registration */}
    <g transform="translate(540 510)">
      <circle cx="0" cy="0" r="9" fill="none" stroke="#0072CE" />
      <circle cx="6" cy="3" r="9" fill="none" stroke="#D6007F" />
      <circle cx="-6" cy="3" r="9" fill="none" stroke="#FFD200" />
      <circle cx="0" cy="-4" r="9" fill="none" stroke="#F5F1E8" />
    </g>
  </svg>
);

const Hero = () => {
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();
  const useR3F = !isMobile && !reduced;

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative w-full overflow-hidden"
      style={{ background: "#0A0A0A", minHeight: "100vh" }}
    >
      <CropMarks color="#C0C0C0" offset={20} length={18} opacity={0.4} />

      {/* faint top CMYK strip */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 flex"
        style={{ height: 3 }}
      >
        <span className="flex-1" style={{ background: "#0072CE" }} />
        <span className="flex-1" style={{ background: "#D6007F" }} />
        <span className="flex-1" style={{ background: "#FFD200" }} />
        <span className="flex-1" style={{ background: "#0A0A0A" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 lg:px-12 pt-32 md:pt-40 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center min-h-[100vh]">
        {/* Text */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.1 }}
          >
            <MicroLabel index={1} accent="#B89B5E">
              The Krister Assembly System
            </MicroLabel>
          </motion.div>

          <motion.h1
            data-testid="hero-headline"
            initial="hidden"
            animate="visible"
            variants={maskReveal}
            transition={{ delay: 0.25 }}
            className="mt-8 md:mt-10"
            style={{
              fontFamily: "Montserrat",
              fontWeight: 900,
              fontSize: "clamp(40px, 7vw, 88px)",
              lineHeight: 0.95,
              letterSpacing: "-0.025em",
              color: "#F5F1E8",
              textTransform: "uppercase",
            }}
          >
            Brand Presence,
            <br />
            Built Piece
            <span style={{ color: "#B89B5E" }}> </span>
            <span style={{ color: "#F5F1E8" }}>By Piece.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl"
            style={{
              color: "#C0C0C0",
              fontSize: "clamp(14px, 1.05vw, 17px)",
              lineHeight: 1.7,
            }}
          >
            Krister Overseas transforms print, gifting, signage, packaging, and
            promotional execution into tangible brand presence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#services"
              data-testid="hero-cta-explore"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("services")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="ko-overline inline-flex items-center gap-3 px-6 py-4 transition-all duration-300 whitespace-nowrap"
              style={{
                background: "#F5F1E8",
                color: "#0A0A0A",
                fontSize: 11,
                letterSpacing: "0.24em",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#B89B5E";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#F5F1E8";
              }}
            >
              Explore Capabilities
              <ArrowRight size={14} strokeWidth={1.5} />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-start"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="ko-overline inline-flex items-center gap-3 px-6 py-4 transition-all duration-300 whitespace-nowrap"
              style={{
                color: "#F5F1E8",
                border: "1px solid rgba(192,192,192,0.4)",
                fontSize: 11,
                letterSpacing: "0.24em",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#D6007F";
                e.currentTarget.style.color = "#F5F1E8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(192,192,192,0.4)";
              }}
            >
              Start a Project
            </a>
          </motion.div>

          {/* meta strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            {[
              { c: "#0072CE", t: "Print Media" },
              { c: "#D6007F", t: "Corporate Gifting" },
              { c: "#FFD200", t: "Brand Visibility" },
              { c: "#B89B5E", t: "Brand Development" },
            ].map((p) => (
              <div
                key={p.t}
                className="flex items-center gap-2"
                style={{ color: "#C0C0C0" }}
              >
                <span
                  aria-hidden
                  style={{ width: 8, height: 8, background: p.c }}
                />
                <span
                  className="ko-overline"
                  style={{ color: "#C0C0C0", fontSize: 10 }}
                >
                  {p.t}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Visual */}
        <div className="lg:col-span-5 relative h-[360px] sm:h-[440px] lg:h-[560px]">
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              border: "1px solid rgba(192,192,192,0.15)",
              pointerEvents: "none",
            }}
          />
          <div className="absolute -top-3 -left-3 z-10">
            <RegistrationDot size={18} color="#0072CE" />
          </div>
          <div className="absolute -bottom-3 -right-3 z-10">
            <RegistrationDot size={18} color="#D6007F" />
          </div>

          <div className="absolute inset-0">
            {useR3F ? (
              <Suspense fallback={<StaticPuzzleScene />}>
                <HeroCanvas />
              </Suspense>
            ) : (
              <StaticPuzzleScene />
            )}
          </div>

          <div
            className="absolute bottom-3 left-3 ko-overline"
            style={{ color: "#B89B5E", zIndex: 5 }}
          >
            CMYK · K · 100
          </div>
          <div
            className="absolute top-3 right-3 ko-overline"
            style={{ color: "#C0C0C0", zIndex: 5 }}
          >
            Plate · 01 / 04
          </div>
        </div>
      </div>

      {/* scroll indicator */}
      <div
        aria-hidden
        className="absolute bottom-6 left-1/2 -translate-x-1/2 ko-overline ko-blink"
        style={{ color: "#C0C0C0" }}
      >
        Scroll · The system assembles
      </div>
    </section>
  );
};

export default Hero;
